import { getHistoryDuration } from '@zuto/utilities'
import HistoryEntry, { HistoryIndex } from './HistoryEntry'

/**
 * Convoluted way to add 1 to a string literal number
 * @param input the starting index
 * @returns the starting index plus 1
 * @throws out of bounds error if new number would be higher than 10
 */
export const incrementIndex = (input: HistoryIndex) : HistoryIndex => {
  switch (input) {
    case '1': return '2'
    case '2': return '3'
    case '3': return '4'
    case '4': return '5'
    case '5': return '6'
    case '6': return '7'
    case '7': return '8'
    case '8': return '9'
    case '9': return '10'
    default: throw new Error('out of bounds')
  }
}

/**
 * Convoluted way to minus 1 to a string literal number
 * @param input the starting index
 * @returns the starting index minus 1
 * @throws out of bounds error if new number would be less than 1
 */
export const decrementIndex = (input: HistoryIndex) : HistoryIndex => {
  switch (input) {
    case '2': return '1'
    case '3': return '2'
    case '4': return '3'
    case '5': return '4'
    case '6': return '5'
    case '7': return '6'
    case '8': return '7'
    case '9': return '8'
    case '10': return '9'
    default: throw new Error('out of bounds')
  }
}

/**
 * Helper function to return the highest value index for a record set
 * @param entries a record set of entries
 * @returns an index with the highest value from the record set provided
 */
export const getLastIndex = <EntryType extends HistoryEntry>(entries: Partial<Record<HistoryIndex, EntryType>>) : HistoryIndex => {
  let lastIndex = 1
  Object.keys(entries).forEach((key) => {
    const parsedKey = Number.parseInt(key, 10)
    if (parsedKey > lastIndex) {
      lastIndex = parsedKey
    }
  })
  return `${lastIndex}` as HistoryIndex
}

/**
 * Evaluates a record set of entries and returns the highest index where the entry has either been added by the form, or is an incomplete entry
 * @param entries a record set of entries
 * @param validate a function to validate an entry
 * @returns the highest index where the entry source is FORM or is an incomplete entry
 */
const getLatestUserAddedOrIncompleteEntryIndex = <EntryType extends HistoryEntry>(
  entries: Partial<Record<HistoryIndex, EntryType>>,
  validate: (entry: EntryType) => boolean
) : HistoryIndex => {
  let startingIndex: HistoryIndex = '1'
  Object.keys(entries).some((key) => {
    const index = key as HistoryIndex
    const entry = entries[index]
    if (entry && (entry.entrySource === 'FORM' || !validate(entry))) {
      startingIndex = index
      return true // breaks out of some()
    }
    startingIndex = incrementIndex(startingIndex)
    return false // continues some()
  })
  return startingIndex
}

/**
 * Returns the correct starting index for the record set and parameters
 * @param entries a record set of entries
 * @param validate a function provided to validate entries
 * @param minimumYearsHistoryRequired the number of years history required overall for the question
 * @param returningViaPrevious whether the user journey is returning to the question via the previous button
 * @param prefillReviewJourney whether the user journey is on the review page
 * @param requiredEntryId The 'Required' ID of the element which was clicked on in order to determine which entry to edit
 * @returns an index that corresponds to a key in the record set where the value is the entry the user should see first
 */
export const getStartingIndex = <EntryType extends HistoryEntry>(
  entries: Partial<Record<HistoryIndex, EntryType>>,
  validate: (entry : EntryType) => boolean,
  minimumYearsHistoryRequired: number,
  returningViaPrevious: boolean,
  prefillReviewJourney: boolean,
  requiredEntryId?: string
) : HistoryIndex => {
  if (requiredEntryId) {
    const requiredRegexPatterns = [
      /^incomplete-employment-(\d+)$/,
      /^required-employment-(\d+)$/,
      /^required-address-(\d+)$/
    ]

    // Find the first matching regular expression pattern in the 'requiredRegexPatterns' array
    // and return the corresponding matching result. The 'find(Boolean)' combination filters out
    // null values, ensuring that the first non-null match result is selected.
    const requiredClickMatch = requiredRegexPatterns.map((pattern) => requiredEntryId.match(pattern)).find(Boolean)

    // If a matching pattern is found, extract a numeric value from it, add 1 to it, and return it as a string. (to determine which entry it needs to return)
    if (requiredClickMatch) {
      return (parseInt(requiredClickMatch[1], 10) + 1).toString() as HistoryIndex
    }
  }
  if (prefillReviewJourney) return '1'
  if (Object.keys(entries).length === 0) return '1'
  if (returningViaPrevious) return getLastIndex(entries)
  const historyDuration = getHistoryDuration(entries)
  if (historyDuration.years && historyDuration.years >= minimumYearsHistoryRequired) return '1'
  return getLatestUserAddedOrIncompleteEntryIndex(entries, validate)
}

/**
 * Create new empty record of entries and then copy from old record up to and including the index
 * @param allEntries The record set of entries to copy from
 * @param upToIndex The index to copy up to and including - can be greater than the length (will just include all)
 * @returns A new record set of entries
 */
export const getEntriesUpToIndex = <EntryType extends HistoryEntry>(
  allEntries: Record<HistoryIndex, EntryType>,
  upToIndex: HistoryIndex
) : Record<HistoryIndex, EntryType> => {
  const filteredEntries : Partial<Record<HistoryIndex, EntryType>> = {}
  const upToIndexAsNumber = Number.parseInt(upToIndex, 10)
  Object.keys(allEntries).forEach((entryIndex: string) => {
    const entryIndexAsHistoryIndex = entryIndex as HistoryIndex
    const entryIndexAsNumber = Number.parseInt(entryIndex, 10)
    if (entryIndexAsNumber <= upToIndexAsNumber) {
      filteredEntries[entryIndexAsHistoryIndex] = allEntries[entryIndexAsHistoryIndex]
    }
  })

  return filteredEntries as Record<HistoryIndex, EntryType>
}

/**
 * Returns true if the given index is the last for the given record set of entries
 * @param entries a record set of entries
 * @param index an index
 * @returns returns true if the index is the last in the record set
 */
export const isLastEntry = <EntryType extends HistoryEntry>(entries: Partial<Record<HistoryIndex, EntryType>>, index: HistoryIndex) : boolean => {
  const entryCount = Object.keys(entries).length
  const parsedIndex = Number.parseInt(index, 10)
  return parsedIndex === entryCount
}
